/**
 * The readings a surface holds, one per domain (ORDR-119).
 *
 * LOADED ON REQUEST, NEVER ON MOUNT BY ITSELF. The caller asks for a module's
 * domains when it opens, which keeps a page load under the GET's 100/minute
 * throttle (the `GrantsPanel` precedent: nothing opens itself).
 *
 * One entry per DOMAIN, not per cadence entry: CAPACITY appears twice (the
 * daily coefficient and "Other"), and both views must show the same reading,
 * including after a save from either.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import type { ApiClient } from "../../api/client.js";
import { createConfigApi, type ConfigApi, type ConfigReading, type Failure } from "./configApi.js";
import type { ConfigDomain } from "./domains.js";

export type GroupState =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly reading: ConfigReading }
  | { readonly status: "failed"; readonly failure: Failure };

export interface Groups {
  readonly api: ConfigApi;
  readonly states: Readonly<Partial<Record<ConfigDomain, GroupState>>>;
  /** Read these domains, skipping any already read or in flight. */
  load(domains: readonly ConfigDomain[]): void;
  /** Read one domain again, whatever its state. */
  reload(domain: ConfigDomain): void;
  /** Adopt the server's reading from a write reply. */
  adopt(domain: ConfigDomain, reading: ConfigReading): void;
}

export function useGroups(client: ApiClient, nodeId: string): Groups {
  const api = useMemo(() => createConfigApi(client), [client]);
  const [states, setStates] = useState<Partial<Record<ConfigDomain, GroupState>>>({});
  // What has been ASKED FOR, synchronously, so a double click cannot double-fetch.
  const requested = useRef(new Set<ConfigDomain>());

  const put = useCallback((domain: ConfigDomain, state: GroupState): void => {
    setStates((prev) => ({ ...prev, [domain]: state }));
  }, []);

  const fetchOne = useCallback(
    async (domain: ConfigDomain, announce: boolean): Promise<void> => {
      // A first read needs no "loading" state: an absent entry renders as
      // loading already, and skipping it keeps `load` free of a synchronous
      // setState, so it is safe to call from an effect.
      if (announce) put(domain, { status: "loading" });
      const outcome = await api.read(nodeId, domain);
      put(domain, outcome.ok ? { status: "ready", reading: outcome.reading } : { status: "failed", failure: outcome });
    },
    [api, nodeId, put],
  );

  const load = useCallback(
    (domains: readonly ConfigDomain[]): void => {
      for (const domain of domains) {
        if (!requested.current.has(domain)) {
          requested.current.add(domain);
          void fetchOne(domain, false);
        }
      }
    },
    [fetchOne],
  );

  const reload = useCallback((domain: ConfigDomain): void => void fetchOne(domain, true), [fetchOne]);
  const adopt = useCallback(
    (domain: ConfigDomain, reading: ConfigReading): void => put(domain, { status: "ready", reading }),
    [put],
  );

  return { api, states, load, reload, adopt };
}
